import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState, useToast } from '@centraoneplus/ui';
import { Pager } from '@shared/ui/kit';
import { Building2, Trash2, Eye, Copy, X, Check } from 'lucide-react';
import { useCustomersStore } from '@stores/customersStore';
import { customerService, organizationService } from '@services/index';
import { useAuthStore } from '@stores/authStore';
import { canDeleteCustomer } from './permissions';
import { useBreakpoint } from '@shared/hooks/useBreakpoint';
import { StatusTabs, RegisterHead, RegisterRow } from './Register';
import { toRegisterRow } from './toRegisterRow';
import type { RegisterSort, StatusTab } from './types';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Customers list view-state is kept in the URL query string so the current
// page / search / filters survive refresh, back/forward, and are bookmarkable
// and shareable. This parses a CustomerFilter (incl. page/limit) from the URL.
// page/limit default to the store defaults so a bare /customers behaves exactly
// as before.
/** The tabs are statuses, and a status tab is the `status` filter the list
 *  endpoint has always taken — the old status cards' behaviour, in the row the
 *  artboard puts it in. */
const STATUS_TABS: StatusTab[] = ['all', 'active', 'inactive', 'blocked'];

// The sort control's options. Every one is a real Customer column, so a
// bookmarked sort can never reach Prisma as an unknown field.
const SORTS: { id: RegisterSort; label: string }[] = [
  { id: 'createdAt', label: 'Date added' },
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'lastSeenAt', label: 'Last seen' },
];
const SORT_IDS = new Set<RegisterSort>(SORTS.map((s) => s.id));

const parseListParams = (sp: URLSearchParams) => {
  const filter: {
    search?: string;
    status?: string;
    tag?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page: number;
    limit: number;
  } = { page: 1, limit: 20 };

  const search = sp.get('search');
  if (search) filter.search = search;
  // The status tab and the status filter are the same thing; there is no second
  // parameter for the tab, so nothing can drift between them.
  const status = sp.get('status');
  if (status) filter.status = status;
  const tag = sp.get('tag');
  if (tag) filter.tag = tag;
  const sortBy = sp.get('sortBy');
  // Only real Customer columns reach the backend: customer-service passes
  // sortBy straight into a Prisma `orderBy`, where an unknown field is a 500.
  if (SORT_IDS.has(sortBy as RegisterSort)) filter.sortBy = sortBy as string;
  const sortOrder = sp.get('sortOrder');
  if (sortOrder === 'asc' || sortOrder === 'desc') filter.sortOrder = sortOrder;

  const page = parseInt(sp.get('page') || '', 10);
  if (Number.isFinite(page) && page > 0) filter.page = page;
  const limit = parseInt(sp.get('limit') || '', 10);
  // Clamp to the API's max (limit must be 1..100 — see customer-service
  // QueryCustomersDto @Max(100)) so a bookmarked/edited ?limit=200 can't 400.
  if (Number.isFinite(limit) && limit > 0) filter.limit = Math.min(limit, 100);

  return filter;
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuthStore();
  // Deleting a customer is admin-only; the BFF enforces the same rule.
  const canDelete = canDeleteCustomer(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    customers,
    total,
    page,
    limit,
    totalPages,
    filters,
    isLoading,
    error,
    fetchCustomers,
    fetchStatusCounts,
    statusCounts,
  } = useCustomersStore();

  const [search, setSearch] = useState(searchParams.get('search') || '');

  // Merge changes into the URL query string (the source of truth for list
  // state). Passing undefined/'' removes a key. Any change here is picked up by
  // the read effect below, which performs the actual fetch — so handlers never
  // fetch directly and we avoid a two-way store<->URL sync loop.
  const updateListParams = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  };
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const tier = useBreakpoint();

  const [organisationCount, setOrganisationCount] = useState<number | null>(null);
  const [sortOpen, setSortOpen] = useState(false);

  // Selection has two modes, and they must never be confused for each other:
  // the ids actually ticked, or "every record matching the current filter",
  // which is a promise about a number the page has not loaded.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  // The active tab IS the status filter — one URL param, so a tab can never
  // disagree with the rows beneath it.
  const activeTab = ((searchParams.get('status') as StatusTab | null) ?? 'all') as StatusTab;
  const sortBy = (searchParams.get('sortBy') as RegisterSort | null) ?? 'createdAt';
  const sortOrder = searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc';

  const rows = useMemo(() => customers.map(toRegisterRow), [customers]);

  const toggleDropdown = (customerId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (openDropdownId === customerId) {
      setOpenDropdownId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_WIDTH = 192; // w-48
    const MENU_HEIGHT = 130; // 3 items + divider
    const openUpward = rect.bottom + MENU_HEIGHT > window.innerHeight;
    setDropdownPos({
      top: openUpward ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpenDropdownId(customerId);
  };

  // Bulk action modals
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll — the menu is fixed-positioned at coordinates captured
  // when it opened, so scrolling the list would otherwise leave it floating detached
  // from its row. Capture phase so scrolling of nested containers is caught too.
  useEffect(() => {
    if (openDropdownId === null) return;
    const handleScroll = () => setOpenDropdownId(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [openDropdownId]);

  // Debounced search → write to the URL (resets to page 1). Compared against the
  // URL's current value so syncing `search` from the URL (back/forward) does not
  // re-trigger a write.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== (searchParams.get('search') || '')) {
        updateListParams({ search: search || undefined, page: undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // URL is the source of truth: (re)fetch whenever the query string changes —
  // covers initial load, refresh, back/forward, and our own param writes. Also
  // keeps the controlled search input in sync on back/forward.
  useEffect(() => {
    const filter = parseListParams(searchParams);
    setSearch(filter.search || '');
    fetchCustomers(filter);
  }, [searchParams]);

  // Status counts only need fetching once on mount.
  useEffect(() => {
    fetchStatusCounts();
  }, []);

  // The organisation total for the header meta. It describes the workspace, not
  // the current filter, so it is fetched once.
  useEffect(() => {
    organizationService
      .getOrganizations({ page: 1, limit: 1 })
      .then((res) => setOrganisationCount(res.total))
      .catch(() => setOrganisationCount(null));
  }, []);

  // AC1: the list must reflect create/edit/delete made elsewhere (another tab,
  // another user, or directly via the API) without a manual refresh. There is
  // no realtime channel for customers, so poll in the background on an interval
  // and refetch *silently* (no spinner, rows kept in place) using the current
  // URL filters. Polling pauses while the user is mid-interaction (a row menu or
  // a bulk/delete modal is open, or a bulk op is running) so the list is never
  // yanked out from under them, and while the tab is hidden to avoid waste.
  const pausePollRef = useRef(false);
  pausePollRef.current =
    openDropdownId !== null ||
    showBulkTagModal ||
    showBulkStatusModal ||
    showDeleteConfirmModal ||
    isBulkProcessing;

  useEffect(() => {
    const POLL_MS = 15000;
    const id = setInterval(() => {
      if (document.hidden || pausePollRef.current) return;
      fetchCustomers(parseListParams(searchParams), { silent: true });
      fetchStatusCounts();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [searchParams, fetchCustomers, fetchStatusCounts]);

  // Clear row selection when the visible page or page-size changes — IDs
  // from the previous page would otherwise stay in `selectedCustomers`
  // and inflate the bulk-action count without any matching rows on the
  // current page.
  useEffect(() => {
    clearSelection();
  }, [page, limit]);

  // Selecting all and then filtering must RE-STATE the count, not keep a stale
  // one: "all 147" stops being true the moment the filter changes, and a bar
  // still promising 147 while the register now holds 23 is the same lie the
  // header checkbox told.
  useEffect(() => {
    setSelectAllMatching(false);
  }, [searchParams]);

  // The header checkbox means THIS PAGE. It has never meant anything else; the
  // bug was that it looked like it did. `SELECT ALL n` in the bar is the only
  // way to act on the whole filtered set, and it states the number.
  const handleSelectAll = (checked: boolean) => {
    setSelectAllMatching(false);
    setSelectedCustomers(checked ? customers.map((c) => c.id) : []);
  };

  const handleSelectCustomer = (customerId: string, checked: boolean) => {
    // Ticking or unticking one row is no longer a claim about the whole set.
    setSelectAllMatching(false);
    // Functional updates, so two quick ticks never read the same stale list.
    setSelectedCustomers((prev) =>
      checked
        ? prev.includes(customerId) ? prev : [...prev, customerId]
        : prev.filter((id) => id !== customerId),
    );
  };

  const clearSelection = () => {
    setSelectedCustomers([]);
    setSelectAllMatching(false);
  };

  const allMatchingSelected = selectAllMatching && selectedCustomers.length > 0;
  /** What a bulk action will actually touch — the number the bar prints. */
  const actionCount = allMatchingSelected ? total : selectedCustomers.length;

  /**
   * Every id matching the current filter, paged out of the list endpoint.
   *
   * `SELECT ALL 147` has to mean 147 records at the moment the action runs, not
   * the 25 the page happens to hold — that gap is how a bulk delete takes out
   * the wrong rows. There is no bulk endpoint, so the ids are resolved here and
   * the per-record calls are made against them.
   */
  const resolveTargetIds = async (): Promise<string[]> => {
    if (!allMatchingSelected) return selectedCustomers;
    const base = parseListParams(searchParams);
    const PAGE = 100;
    const ids: string[] = [];
    for (let p = 1; p <= Math.ceil(total / PAGE) && p <= 100; p += 1) {
      const res = await customerService.getCustomers({ ...base, page: p, limit: PAGE });
      ids.push(...res.data.map((c) => c.id));
      if (res.data.length < PAGE) break;
    }
    return ids;
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // #18944 — export the rows the user is actually looking at. Mirror the
      // list's active search / status / tag (the same params parseListParams
      // feeds the list query) instead of only status, which exported everyone.
      const listFilters = parseListParams(searchParams);
      const result = await customerService.exportCustomers({
        format: 'csv',
        filters: {
          status: listFilters.status,
          search: listFilters.search,
          tags: listFilters.tag ? [listFilters.tag] : undefined,
        },
      });

      // Decode base64 to raw bytes. atob() returns a Latin-1 "binary string"
      // (one char per byte); building a Blob from that string would re-encode
      // it as UTF-8 and double-encode every non-ASCII byte (é -> Ã©). Map each
      // char code back to its byte so the server's UTF-8 (incl. the BOM) is
      // preserved verbatim.
      const binary = atob(result.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(
        'Export failed',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Could not export customers. Please try again.',
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkAddTags = async () => {
    if (!bulkTagInput.trim() || selectedCustomers.length === 0) return;
    setIsBulkProcessing(true);
    try {
      const tags = bulkTagInput.split(',').map((t) => t.trim()).filter(Boolean);
      const targets = await resolveTargetIds();
      const count = targets.length;
      await Promise.all(
        targets.map((customerId) =>
          customerService.updateCustomer(customerId, {
            tags: [...(customers.find((c) => c.id === customerId)?.tags || []), ...tags],
          })
        )
      );
      setShowBulkTagModal(false);
      setBulkTagInput('');
      clearSelection();
      fetchCustomers();
      toast.success('Tags added', `Added tags to ${count} customer(s)`);
    } catch (err) {
      console.error('Failed to add tags:', err);
      toast.error(
        'Failed to add tags',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.',
      );
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkChangeStatus = async () => {
    if (!bulkStatus || selectedCustomers.length === 0) return;
    setIsBulkProcessing(true);
    try {
      const targets = await resolveTargetIds();
      const count = targets.length;
      await Promise.all(
        targets.map((customerId) =>
          customerService.updateCustomer(customerId, { status: bulkStatus })
        )
      );
      setShowBulkStatusModal(false);
      setBulkStatus('');
      clearSelection();
      fetchCustomers();
      // The tabs count statuses, so a bulk status change moves the tab counts.
      fetchStatusCounts();
      toast.success('Status updated', `Updated ${count} customer(s)`);
    } catch (err) {
      console.error('Failed to change status:', err);
      toast.error(
        'Failed to update status',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.',
      );
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    const idsToDelete = customerToDelete ? [customerToDelete] : await resolveTargetIds();
    if (idsToDelete.length === 0) return;
    setIsBulkProcessing(true);
    try {
      // Use allSettled so one failure (e.g. the backend's 409 when a customer
      // still has active/open tickets) doesn't hide the outcome of the others.
      const results = await Promise.allSettled(
        idsToDelete.map((customerId) => customerService.deleteCustomer(customerId))
      );
      const rejected = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      const succeededCount = results.length - rejected.length;

      // Refresh the list/counts if anything was actually deleted.
      if (succeededCount > 0) {
        if (!customerToDelete) clearSelection();
        fetchCustomers();
        fetchStatusCounts();
      }

      // Always close the confirmation modal first — on success AND failure — so
      // the dialog is dismissed before the feedback toast appears, instead of
      // the error toast popping up while the modal is still open.
      setShowDeleteConfirmModal(false);
      setCustomerToDelete(null);

      if (succeededCount > 0) {
        toast.success('Customer(s) deleted');
      }

      if (rejected.length > 0) {
        // Surface the backend's reason instead of failing silently.
        const reason =
          (rejected[0].reason as { response?: { data?: { message?: string } }; message?: string })
            ?.response?.data?.message ||
          (rejected[0].reason as { message?: string })?.message ||
          'Failed to delete customer.';
        const message =
          rejected.length === 1
            ? reason
            : `${rejected.length} of ${results.length} customer(s) could not be deleted. ${reason}`;
        toast.error('Could not delete customer', message);
      }
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteConfirmModal(false);
    setCustomerToDelete(null);
    setDeleteConfirmInput('');
  };

  const handleExportSelected = async () => {
    if (selectedCustomers.length === 0) return;
    setIsExporting(true);
    try {
      // Export what is loaded — the CSV is built from rows in hand, so this one
      // action deliberately does not follow `SELECT ALL n`. Use the header
      // Export for the whole filtered register.
      const selectedData = customers.filter((c) => selectedCustomers.includes(c.id));
      const csvContent = [
        ['Name', 'Email', 'Phone', 'Status', 'Tags', 'Created At'].join(','),
        ...selectedData.map((c) =>
          [
            c.name || '',
            c.email,
            c.phone || '',
            c.status,
            c.tags.join(';'),
            c.createdAt,
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewCustomer = (customerId: string) => {
    // Pass the list's (uncached, fresh) status into the profile so its blocked
    // banner + feature-gating are correct even if the customer-detail read is
    // momentarily stale.
    const status = customers.find((c) => c.id === customerId)?.status;
    navigate(`/customers/${customerId}`, { state: { status } });
    setOpenDropdownId(null);
  };

  const handleDeleteSingleCustomer = (customerId: string) => {
    setCustomerToDelete(customerId);
    setShowDeleteConfirmModal(true);
    setOpenDropdownId(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email?.slice(0, 2).toUpperCase() || '??';
  };

  const clearFilters = () => {
    setSearch('');
    setSearchParams(new URLSearchParams());
  };

  // Only things that NARROW the register count as filters. Paging and sorting
  // live in the URL too, and `status` is the tab rather than a filter — counting
  // any of them would badge the Filters button on a bare /customers and make
  // every empty state offer "clear filters" when there is nothing to clear.
  const NON_FILTER_KEYS = new Set(['page', 'limit', 'sortBy', 'sortOrder', 'status']);
  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => !NON_FILTER_KEYS.has(key) && value,
  ).length;
  const hasActiveFilters = !!search || activeFilterCount > 0;

  // The active filters, as chips — one per Filters-panel field that is off its
  // default, so the header always says exactly how the list was narrowed or
  // ordered. Every `clear` writes the same URL param its field writes, so
  // removing a chip and resetting the field are one operation.
  const isAlphaSort = sortBy === 'name' || sortBy === 'email';
  const orderLabel = (order: 'asc' | 'desc') =>
    isAlphaSort
      ? order === 'asc' ? 'A → Z' : 'Z → A'
      : order === 'asc' ? 'Oldest first' : 'Newest first';
  const STATUS_LABEL: Record<StatusTab, string> = {
    all: 'All statuses',
    active: 'Active',
    inactive: 'Inactive',
    blocked: 'Blocked',
  };

  const urlSearch = searchParams.get('search') ?? '';
  const activeChips: { key: string; label: string; value: string; clear: () => void }[] = [];
  if (urlSearch) {
    activeChips.push({
      key: 'search',
      label: 'Search',
      value: urlSearch,
      // Both halves, not just the URL: the field holds its own state and the
      // 300ms debounce would write the stale term straight back afterwards.
      clear: () => {
        setSearch('');
        updateListParams({ search: undefined, page: undefined });
      },
    });
  }
  if (activeTab !== 'all' && STATUS_TABS.includes(activeTab)) {
    activeChips.push({
      key: 'status',
      label: 'Status',
      value: STATUS_LABEL[activeTab],
      clear: () => updateListParams({ status: undefined, page: undefined }),
    });
  }
  if (filters.tag) {
    activeChips.push({
      key: 'tag',
      label: 'Tag',
      value: filters.tag,
      clear: () => updateListParams({ tag: undefined, page: undefined }),
    });
  }
  if (sortBy !== 'createdAt') {
    activeChips.push({
      key: 'sortBy',
      label: 'Sort by',
      value: SORTS.find((s) => s.id === sortBy)?.label ?? sortBy,
      clear: () => updateListParams({ sortBy: undefined, page: undefined }),
    });
  }
  if (sortOrder === 'asc') {
    activeChips.push({
      key: 'sortOrder',
      label: 'Order',
      value: orderLabel('asc'),
      clear: () => updateListParams({ sortOrder: undefined, page: undefined }),
    });
  }

  // Resets exactly the fields the chips show — search, status, tag, sort and
  // order — back to their defaults. Page size is kept.
  const clearChipFilters = () => {
    setSearch('');
    updateListParams({
      search: undefined,
      status: undefined,
      tag: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      page: undefined,
    });
  };

  // 26b's shell: cards float ON the canvas, controls are 34px and rounded, and
  // the accent carries hover. Only the CHROME moves — every column, handler,
  // filter and bulk action on this page is the one that was here before.
  const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
  const PANEL = 'border border-[var(--co-line)] bg-[var(--co-raised)]';
  const FIELD =
    'h-[38px] w-full min-w-0 rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] px-3 text-[13px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] hover:border-[var(--co-line-strong)] focus:border-[var(--co-brass)] focus:outline-none focus:ring-2 focus:ring-[var(--co-selected)]';
  const FIELD_LABEL = 'text-[12.5px] font-medium text-[var(--co-ink-2)]';
  const CONTROL =
    'flex h-[34px] flex-none items-center gap-2 whitespace-nowrap rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-selected)]';
  // Buttons ON the ink bar: they sit on --co-ink, so they take the on-ink line
  // rather than the paper-weight border, which disappears against it.
  const INK_BUTTON =
    'flex h-[30px] flex-none items-center rounded-full border border-[var(--co-on-ink-line)] px-[14px] text-[12.5px] text-[var(--co-on-ink)] hover:bg-[var(--co-brass-hover)] disabled:opacity-50';
  // With rows on screen the register card fills the column and scrolls its own
  // rows, so its rounded foot is always drawn instead of being sliced off by
  // the column's scroll edge.
  const hasRows = !isLoading && customers.length > 0;

  // Above twenty, the count must be typed. Twenty is where "I meant to do that"
  // stops being obvious from the dialog alone.
  const deleteCount = customerToDelete ? 1 : actionCount;
  const needsTypedConfirm = !customerToDelete && deleteCount > 20;
  const typedConfirmOk = deleteConfirmInput.trim() === String(deleteCount);

  return (
    <div
      className="co-customers flex h-full min-h-0 flex-col gap-[14px] bg-[var(--co-canvas)] py-4 pr-4"
      data-tier={tier}
    >
      {/* ── Head card, §2.1 in 26b's shell ───────────────────────────────── */}
      <div className={`flex-none ${PANEL} px-5 py-[18px]`} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + counts on the left, the utility cluster on the right. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.08] tracking-[-.018em] text-[var(--co-ink)]">
              Customers
            </h1>
            <span className="mt-[6px] block font-mono text-[10.5px] font-medium tracking-[.13em] text-[var(--co-ink-2)]">
              {`${total} ${total === 1 ? 'PERSON' : 'PEOPLE'}`}
              {organisationCount !== null &&
                ` · ${organisationCount} ORGANISATION${organisationCount === 1 ? '' : 'S'}`}
            </span>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: every other header control, wrapping when space runs out. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
            {/* The placeholder names all four things the search matches. A bare
                "Search" is why people paste a domain into the org filter. */}
            <div className="relative min-w-0 flex-none" style={{ width: 270, maxWidth: '100%' }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--co-ink-4)"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
                className="pointer-events-none absolute left-[10px] top-1/2 -translate-y-1/2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, phone or domain"
                aria-label="Search customers by name, email, phone or domain"
                className="h-[34px] w-full rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] pl-[31px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--co-ink-4)] hover:text-[var(--co-ink)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={CONTROL}
              style={showFilters ? { borderColor: 'var(--co-ink)', color: 'var(--co-ink)' } : undefined}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="font-mono text-[10px] text-[var(--co-ink-4)]">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className="flex-1" />
            <Link to="/customers/import" className={CONTROL}>
              Import
            </Link>
            <button type="button" onClick={handleExport} disabled={isExporting} className={CONTROL}>
              {isExporting ? 'Exporting…' : 'Export'}
            </button>
            {/* The one filled button on the screen. */}
            <Link
              to="/customers/new"
              className="flex h-[34px] flex-none items-center rounded-[10px] bg-[var(--co-brass)] px-4 text-[12.5px] font-bold text-[var(--co-brass-on)] hover:bg-[var(--co-brass-hover)]"
            >
              New customer
            </Link>
        </div>

        <StatusTabs
          active={STATUS_TABS.includes(activeTab) ? activeTab : 'all'}
          counts={statusCounts}
          total={activeTab === 'all' ? total : null}
          onSelect={(tab) =>
            updateListParams({
              status: tab === 'all' ? undefined : tab,
              page: undefined,
            })
          }
          sortLabel={SORTS.find((s) => s.id === sortBy)?.label ?? 'Date added'}
          onOpenSort={() => setSortOpen((v) => !v)}
        />

        {/* Sort menu — a select-shaped button's list, never a native <select>. */}
        {sortOpen && (
          <div className="relative">
            <ul
              role="listbox"
              className="absolute right-0 top-1 z-20 w-[224px] overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)] py-1"
              style={CARD}
            >
              {SORTS.map((s) => {
                const on = s.id === sortBy;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => {
                        updateListParams({ sortBy: s.id, page: undefined });
                        setSortOpen(false);
                      }}
                      className="flex h-[34px] w-full items-center gap-[9px] px-3 text-left text-[12.5px] hover:bg-[var(--co-selected)]"
                      style={{
                        fontWeight: on ? 600 : 400,
                        color: on ? 'var(--co-brass-ink)' : 'var(--co-ink)',
                      }}
                    >
                      {/* The tick reserves its box on every row, so the labels
                          stay on one left edge instead of stepping sideways as
                          the selection moves. */}
                      <span className="flex h-[14px] w-[14px] flex-none items-center justify-center">
                        {on && <Check className="h-[13px] w-[13px]" />}
                      </span>
                      {s.label}
                    </button>
                  </li>
                );
              })}
              <li className="mt-1 border-t border-[var(--co-line)] pt-1">
                <button
                  type="button"
                  onClick={() => {
                    updateListParams({
                      sortOrder: sortOrder === 'asc' ? undefined : 'asc',
                      page: undefined,
                    });
                    setSortOpen(false);
                  }}
                  className="flex h-[34px] w-full items-center gap-[9px] px-3 pl-[36px] text-left font-mono text-[10px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink-3)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
                >
                  {/* Named for the column being sorted: "OLDEST FIRST" on a
                      date and "A → Z" on a name say the same thing about
                      direction, but only one of them reads as English. */}
                  {sortBy === 'name' || sortBy === 'email'
                    ? sortOrder === 'asc'
                      ? 'SORT Z → A'
                      : 'SORT A → Z'
                    : sortOrder === 'asc'
                      ? 'NEWEST FIRST'
                      : 'OLDEST FIRST'}
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* Kept in the head card rather than above the rows: a filter you
            cannot see is a filter you blame the data for, and anything in the
            scrolling column is invisible by row twenty. */}
        {activeChips.length > 0 && (
          <div className="mt-[14px] flex flex-wrap items-center gap-2 border-t border-[var(--co-line)] pt-[13px]">
            <span className="co-mono flex-none text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Filtered by
            </span>
            {activeChips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex max-w-full items-center gap-[7px] rounded-full border border-[var(--co-brass-line)] bg-[var(--co-selected)] py-[4px] pl-[11px] pr-[5px] text-[12px]"
              >
                <span className="co-mono flex-none text-[9px] font-semibold uppercase tracking-[.12em] text-[var(--co-brass-ink)]">
                  {chip.label}
                </span>
                <span className="max-w-[220px] truncate text-[var(--co-ink)]" title={chip.value}>
                  {chip.value}
                </span>
                <button
                  type="button"
                  onClick={chip.clear}
                  aria-label={`Remove ${chip.label.toLowerCase()} filter`}
                  className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[var(--co-ink-3)] hover:bg-[var(--co-brass)] hover:text-[var(--co-brass-on)]"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {activeChips.length > 1 && (
              <button
                type="button"
                onClick={clearChipFilters}
                className="co-mono ml-1 flex-none text-[10px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink-3)] hover:text-[var(--co-ink)] hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Positioning frame for the bulk bar: it floats over the register's foot
          instead of taking a row in the column, so the pager never moves. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {showFilters && (
          <div className={`mb-[14px] flex-none ${PANEL} px-5 py-4`} style={CARD}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className={FIELD_LABEL}>Status</span>
                <ThemedSelect
                  value={STATUS_TABS.includes(activeTab) ? activeTab : 'all'}
                  onChange={(e) =>
                    updateListParams({
                      status: e.target.value === 'all' ? undefined : e.target.value,
                      page: undefined,
                    })
                  }
                  className={FIELD}
                >
                  {STATUS_TABS.map((t) => (
                    <option key={t} value={t}>
                      {STATUS_LABEL[t]}
                    </option>
                  ))}
                </ThemedSelect>
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className={FIELD_LABEL}>Tag</span>
                <input
                  type="text"
                  placeholder="Filter by tag…"
                  value={filters.tag || ''}
                  onChange={(e) => updateListParams({ tag: e.target.value || undefined, page: undefined })}
                  className={FIELD}
                />
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className={FIELD_LABEL}>Sort by</span>
                <ThemedSelect
                  value={sortBy}
                  onChange={(e) =>
                    updateListParams({
                      sortBy: e.target.value === 'createdAt' ? undefined : e.target.value,
                      page: undefined,
                    })
                  }
                  className={FIELD}
                >
                  {SORTS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </ThemedSelect>
              </label>

              <label className="flex min-w-0 flex-col gap-1.5">
                <span className={FIELD_LABEL}>Order</span>
                <ThemedSelect
                  value={sortOrder}
                  onChange={(e) =>
                    updateListParams({
                      sortOrder: e.target.value === 'asc' ? 'asc' : undefined,
                      page: undefined,
                    })
                  }
                  className={FIELD}
                >
                  <option value="desc">{orderLabel('desc')}</option>
                  <option value="asc">{orderLabel('asc')}</option>
                </ThemedSelect>
              </label>
            </div>
          </div>
        )}

        {error && (
          <div
            className="mb-[14px] flex-none border px-4 py-3 text-[12.5px]"
            style={{
              borderRadius: 10,
              borderColor: 'var(--co-burgundy-line)',
              background: 'var(--co-burgundy-wash)',
              color: 'var(--co-burgundy)',
            }}
          >
            {error}
          </div>
        )}

      {/* ── The register, §2.3–§2.4 ────────────────────────────────────── */}
      <div
        ref={dropdownRef}
        className={`${hasRows ? 'flex min-h-[240px] flex-1 flex-col' : 'flex-none'} ${PANEL} overflow-hidden`}
        style={CARD}
      >
        {isLoading ? (
          <div className="p-12 text-center font-mono text-[11px] tracking-[.14em] text-[var(--co-ink-4)]">
            LOADING THE REGISTER…
          </div>
        ) : customers.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-12 w-12" />}
            title={
              activeTab !== 'all'
                ? `No ${activeTab} customers`
                : 'No customers found'
            }
            description={
              hasActiveFilters || activeTab !== 'all'
                ? 'Nothing in this view. The register itself may still have records.'
                : 'Add your first customer to get started'
            }
            action={
              hasActiveFilters || activeTab !== 'all' ? (
                <button onClick={clearFilters} className="btn btn-outline">
                  Clear filters
                </button>
              ) : (
                <Link to="/customers/new" className="btn btn-primary">
                  Add Customer
                </Link>
              )
            }
          />
        ) : (
          <div role="table" aria-label="Customer register" className="flex min-h-0 flex-1 flex-col">
            <RegisterHead
              tier={tier}
              allSelected={
                selectedCustomers.length > 0 && selectedCustomers.length === customers.length
              }
              onSelectAllOnPage={handleSelectAll}
              sortBy={sortBy}
              sortOrder={sortOrder}
              // Bug 9164 — click-to-sort on the column headers, same
              // mechanism the Filters panel's "Sort By" already uses: a
              // click on the ACTIVE column toggles order (identical to the
              // "Oldest/Newest First" · "Sort A→Z/Z→A" button), a click on a
              // DIFFERENT column switches sortBy and keeps the current order
              // (identical to picking a new option in the Sort By list).
              onSort={(field) => {
                if (field === sortBy) {
                  updateListParams({ sortOrder: sortOrder === 'asc' ? undefined : 'asc', page: undefined });
                } else {
                  updateListParams({ sortBy: field, page: undefined });
                }
              }}
            />
            {/* Bottom padding while the bulk bar floats here, so the last row
                can still scroll clear of it. */}
            <div
              role="rowgroup"
              className={`min-h-0 flex-1 overflow-y-auto ${selectedCustomers.length > 0 ? 'pb-[88px]' : ''}`}
            >
              {rows.map((row) => (
                <RegisterRow
                  key={row.id}
                  row={row}
                  tier={tier}
                  selected={selectedCustomers.includes(row.id)}
                  onToggle={(checked) => handleSelectCustomer(row.id, checked)}
                  onOpen={() => handleViewCustomer(row.id)}
                  onMenu={(e) => toggleDropdown(row.id, e)}
                />
              ))}
            </div>
          </div>
        )}
        {openDropdownId && dropdownPos && createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              ...CARD,
            }}
            className="z-50 w-[190px] overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)] py-1"
          >
            <button
              onClick={() => handleViewCustomer(openDropdownId)}
              className="flex h-[34px] w-full items-center gap-[9px] px-3 text-left text-[12.5px] text-[var(--co-ink-2)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
            >
              <Eye className="h-3.5 w-3.5" />
              View details
            </button>
            <button
              onClick={() => {
                const email = customers.find((c) => c.id === openDropdownId)?.email;
                if (email) navigator.clipboard.writeText(email);
                setOpenDropdownId(null);
              }}
              className="flex h-[34px] w-full items-center gap-[9px] px-3 text-left text-[12.5px] text-[var(--co-ink-2)] hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy email
            </button>
            {canDelete && (
              <>
                <div className="my-1 h-px bg-[var(--co-line)]" />
                <button
                  onClick={() => handleDeleteSingleCustomer(openDropdownId)}
                  className="flex h-[34px] w-full items-center gap-[9px] px-3 text-left text-[12.5px] hover:bg-[var(--co-burgundy-wash)]"
                  style={{ color: 'var(--co-burgundy)' }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
      </div>

      </div>

      {/* ── 11b, the bulk bar. Floats over the register's foot (absolute in
          the frame above), never pushing the table or the pager.
          `SELECT ALL n` states the TRUE total, because the header checkbox
          selecting only the loaded page while appearing to select everything is
          how a bulk delete takes out the wrong records. ─────────────────── */}
      {selectedCustomers.length > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-30 flex justify-center px-6">
          <div
            className="pointer-events-auto flex w-full max-w-[860px] flex-wrap items-center gap-4 rounded-full bg-[var(--co-ink)] py-[10px] pl-6 pr-3"
            style={{ boxShadow: '0 12px 32px -8px rgba(0,0,0,.35), 0 2px 6px rgba(0,0,0,.12)' }}
          >
            <span
              className="h-[12px] w-[12px] flex-none rounded-full"
              style={{ background: 'var(--co-brass-pale)' }}
              aria-hidden="true"
            />
            <span className="flex-none font-mono text-[12.5px] font-semibold text-[var(--co-on-ink)]">
              {`${allMatchingSelected ? total : selectedCustomers.length} SELECTED`}
            </span>
            {/* Always the true total, re-stated when the filter changes. */}
            {!allMatchingSelected && total > selectedCustomers.length && (
              <button
                type="button"
                onClick={() => setSelectAllMatching(true)}
                className="flex-none font-mono text-[10px] font-medium tracking-[.12em] text-[var(--co-brass-pale)] hover:underline"
              >
                {`SELECT ALL ${total}`}
              </button>
            )}
            {allMatchingSelected && (
              <span className="flex-none font-mono text-[10px] tracking-[.12em] text-[var(--co-on-ink-2)]">
                EVERY MATCHING RECORD
              </span>
            )}

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setShowBulkTagModal(true)} className={INK_BUTTON}>
                Add tags
              </button>
              <button type="button" onClick={() => setShowBulkStatusModal(true)} className={INK_BUTTON}>
                Change status
              </button>
              <button type="button" onClick={handleExportSelected} disabled={isExporting} className={INK_BUTTON}>
                Export
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmModal(true)}
                  className="flex h-[30px] items-center rounded-full border px-[14px] text-[12.5px]"
                  style={{ borderColor: 'var(--co-burgundy)', color: 'var(--co-burgundy-pale)' }}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={clearSelection}
                aria-label="Clear selection"
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full text-[13px] text-[var(--co-on-ink-2)] hover:bg-[var(--co-on-ink-line)] hover:text-[var(--co-on-ink)]"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Pinned, not paged past. The pager was inside the scrolling column, so
          on a full page of rows you had to scroll to the bottom to reach the
          control that moves you off the bottom. */}
      {customers.length > 0 && (
        <div className={`co-pagefoot flex-none ${PANEL} px-5`} style={CARD}>
          <Pager
            id="customers"
            showRange
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p: number) => updateListParams({ page: p > 1 ? String(p) : undefined })}
            totalItems={total}
            pageSize={limit}
            onPageSizeChange={(size: number) => updateListParams({ limit: String(size), page: undefined })}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </div>
      )}


      {/* Bulk Add Tags Modal */}
      {showBulkTagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBulkTagModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {`Add tags to ${actionCount} customer${actionCount === 1 ? '' : 's'}`}
            </h3>
            <input
              type="text"
              placeholder="Enter tags separated by commas..."
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
              className="input w-full mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowBulkTagModal(false);
                  setBulkTagInput('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkAddTags}
                disabled={isBulkProcessing || !bulkTagInput.trim()}
              >
                {isBulkProcessing ? 'Adding...' : 'Add Tags'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Change Status Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBulkStatusModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {`Change status for ${actionCount} customer${actionCount === 1 ? '' : 's'}`}
            </h3>
            {/* Three options is a segmented control, not a dropdown — and the
                native <ThemedSelect> brings the browser's own chrome into a hueless
                design (phase 23 §9). */}
            <div
              role="radiogroup"
              aria-label="New status"
              className="mb-4 flex h-[34px] items-center border border-[var(--co-line-strong)]"
            >
              {(['active', 'inactive', 'blocked'] as const).map((s, i) => {
                const on = bulkStatus === s;
                return (
                  <button
                    key={s}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setBulkStatus(s)}
                    className="flex h-full flex-1 items-center justify-center font-mono text-[10px] font-semibold tracking-[.1em]"
                    style={{
                      background: on ? 'var(--co-ink)' : 'transparent',
                      color: on ? 'var(--co-ink-invert)' : 'var(--co-ink-3)',
                      borderLeft: i ? '1px solid var(--co-line-strong)' : undefined,
                    }}
                  >
                    {s.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowBulkStatusModal(false);
                  setBulkStatus('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkChangeStatus}
                disabled={isBulkProcessing || !bulkStatus}
              >
                {isBulkProcessing ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeDeleteModal} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {customerToDelete ? 'Delete Customer' : 'Delete Customers'}
            </h3>
            {/* The count is the point of this dialog. It states what will
                actually be deleted — `SELECT ALL n` included — because the
                gap between "what I ticked" and "what runs" is where a bulk
                delete goes wrong. */}
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete{' '}
              <strong>
                {customerToDelete
                  ? 'this customer'
                  : `${deleteCount} customer${deleteCount === 1 ? '' : 's'}`}
              </strong>
              ? This action cannot be undone.
            </p>
            {needsTypedConfirm && (
              <label className="mb-4 block">
                <span className="mb-1 block text-sm text-gray-600 dark:text-gray-400">
                  {`Type ${deleteCount} to confirm`}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  aria-label={`Type ${deleteCount} to confirm the deletion`}
                  className="input w-full"
                />
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-outline"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                onClick={handleConfirmDelete}
                disabled={isBulkProcessing || (needsTypedConfirm && !typedConfirmOk)}
                title={
                  needsTypedConfirm && !typedConfirmOk
                    ? `Type ${deleteCount} to confirm`
                    : undefined
                }
              >
                {isBulkProcessing ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
